// frontend_react/src/pages/main/EventsPublicPage.jsx

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getCalendarEvents, getPublicEventIcsUrl } from "../../services/calendarApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";

export default function EventsPublicPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();

  const upcomingEvents = useMemo(() => {
    const now = new Date();

    return events
      .filter((event) => new Date(event.start_timestamp) >= now)
      .sort(
        (a, b) =>
          new Date(a.start_timestamp).getTime() -
          new Date(b.start_timestamp).getTime()
      );
  }, [events]);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      setError("");

      try {
        const { data } = await getCalendarEvents({
          companyUuid: selectedCompanyUuid,
        });
        const list = Array.isArray(data) ? data : data?.events || [];
        setEvents(list);
      } catch (err) {
        logger.error(err);
        setError("Impossible de charger les événements.");
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [selectedCompanyUuid]);

  const formatDateTime = (start, end) => {
    if (!start) return "Date à confirmer";

    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;

    const date = startDate.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const startHour = startDate.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const endHour = endDate
      ? endDate.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

    return endHour ? `${date} • ${startHour} - ${endHour}` : `${date} • ${startHour}`;
  };

  return (
    <PageWrapper>
      <PageHeader>
        <SectionBadge>Événements</SectionBadge>
        <h1>Les événements Queen&apos;s Games</h1>
        <p>
          Retrouve les animations, soirées jeux, ateliers et événements à venir.
          Tu peux t’inscrire directement en ligne.
        </p>
      </PageHeader>

      {loading && <PanelInfo>Chargement des événements...</PanelInfo>}
      {error && <PanelMessage $error>{error}</PanelMessage>}

      {!loading && !error && upcomingEvents.length === 0 && (
        <PanelInfo>Aucun événement prévu pour le moment.</PanelInfo>
      )}

      <EventsGrid>
        {upcomingEvents.map((event) => (
          <EventCard key={event.uuid}>
            <CardHeader>
              <SectionBadge>{event.event_type || "Événement"}</SectionBadge>
              <h2>{event.title}</h2>
            </CardHeader>

            <EventMeta>
              <span>{formatDateTime(event.start_timestamp, event.end_timestamp)}</span>
              <span>{event.location || "Lieu à confirmer"}</span>
              <span>
                {Number(event.total_participants || 0)} inscrit(s)
              </span>
              <span>
                Places : {event.capacity_label || `${event.total_participants || 0}/${event.max_participants || "∞"}`}
              </span>
            </EventMeta>

            {event.payment_required && (
              <InlineInfo>
                Paiement requis : {event.payment_amount_ttc || "montant à confirmer"} {event.payment_currency || "EUR"}
              </InlineInfo>
            )}

            {event.description && (
              <Description>{event.description}</Description>
            )}

            {event.url && (
              <ExternalLink href={event.url} target="_blank" rel="noreferrer">
                Lien de l’événement
              </ExternalLink>
            )}

            <Actions>
              <SubmitButton
                type="button"
                onClick={() => navigate(`/event/${event.uuid}/register`)}
              >
                S’inscrire
              </SubmitButton>

              <GhostButton
                type="button"
                onClick={() =>
                  window.open(
                    getPublicEventIcsUrl(event.uuid, selectedCompanyUuid),
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Ajouter au calendrier
              </GhostButton>
            </Actions>
          </EventCard>
        ))}
      </EventsGrid>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  padding: 1rem;
  background: #0b1328;
  color: #f7f7f7;
`;

const PageHeader = styled.header`
  max-width: 980px;
  margin: 0 auto 1rem;
  padding: 1rem;

  h1 {
    margin: 0.55rem 0 0.35rem;
    font-size: clamp(1.45rem, 4vw, 2rem);
    font-weight: 950;
  }

  p {
    margin: 0;
    opacity: 0.76;
    line-height: 1.45;
  }
`;

const EventsGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;

  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(340px, 380px)
  );

  justify-content: center;
  align-items: stretch;

  gap: 1rem;
  padding: 0 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: minmax(280px, 1fr);
  }
`;

const EventCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);

  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardHeader = styled.div`
  margin-bottom: 0.9rem;

  h2 {
    margin: 0.45rem 0 0;
    font-size: 1.1rem;
    font-weight: 950;
    color: #f7f7f7;
  }
`;

const SectionBadge = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const EventMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  opacity: 0.84;
  font-size: 0.9rem;
`;

const Description = styled.p`
  margin: 0.9rem 0 0;
  opacity: 0.82;
  line-height: 1.45;
`;

const ExternalLink = styled.a`
  display: inline-block;
  margin-top: 0.8rem;
  color: #fff9ea;
  font-weight: 850;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;
  margin-top: 1rem;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;
`;

const GhostButton = styled(SubmitButton)`
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  border: 1px solid rgba(255, 249, 234, 0.35);
`;

const PanelMessage = styled.div`
  max-width: 980px;
  margin: 0 auto 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  max-width: 980px;
  margin: 0 auto 1rem;
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  opacity: 0.85;
`;

const InlineInfo = styled.div`
  margin-top: 0.85rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.12);
  border: 1px solid rgba(255, 249, 234, 0.25);
  color: #fff9ea;
  font-weight: 850;
`;